import * as React from 'react';

let youTubeApiPromise;

function loadYouTubeApi() {
  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }

  if (youTubeApiPromise) {
    return youTubeApiPromise;
  }

  youTubeApiPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    const previousReadyHandler = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      previousReadyHandler?.();
      resolve(window.YT);
    };

    if (existingScript) {
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    script.onerror = () => {
      youTubeApiPromise = undefined;
      reject(new Error("Failed to load the YouTube iframe API."));
    };

    document.body.appendChild(script);
  });

  return youTubeApiPromise;
}

function YouTubePreview({ videoId, title, className }) {
  return (
    <div
      aria-label={title}
      className={className}
      data-youtube-preview={videoId}
      role="img"
      style={{ position: "relative", overflow: "hidden" }}
    >
      <img
        alt={title}
        src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover"
        }}
      />
    </div>
  );
}

function YouTubePlayer({
  videoId,
  title,
  className,
  isActive,
  isPlaybackEnabled,
  onPlaybackEnd,
  hasUserInteracted
}) {
  const playerContainerRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const playerReadyRef = React.useRef(false);
  const loadedVideoIdRef = React.useRef(videoId);
  const videoIdRef = React.useRef(videoId);
  const onPlaybackEndRef = React.useRef(onPlaybackEnd);
  const hasUserInteractedRef = React.useRef(hasUserInteracted);
  const isActiveRef = React.useRef(isActive);
  const isPlaybackEnabledRef = React.useRef(isPlaybackEnabled);
  const pendingUnmuteRef = React.useRef(false);
  const autoplayRetryRef = React.useRef(false);
  const errorVideoIdRef = React.useRef(null);
  const [hasStartedPlayback, setHasStartedPlayback] = React.useState(false);

  videoIdRef.current = videoId;
  onPlaybackEndRef.current = onPlaybackEnd;
  hasUserInteractedRef.current = hasUserInteracted;
  isActiveRef.current = isActive;
  isPlaybackEnabledRef.current = isPlaybackEnabled;

  const startPlayback = React.useCallback((player, shouldLoadVideo) => {
    if (!player || !playerReadyRef.current) {
      return;
    }

    if (!isPlaybackEnabledRef.current || !isActiveRef.current) {
      player.pauseVideo?.();
      return;
    }

    // Start through the browser-safe muted path, then restore sound after
    // YouTube reports PLAYING if the user has already interacted.
    player.mute?.();
    pendingUnmuteRef.current = hasUserInteractedRef.current;

    if (shouldLoadVideo) {
      loadedVideoIdRef.current = videoIdRef.current;
      player.loadVideoById?.(videoIdRef.current);
      return;
    }

    player.playVideo?.();
  }, []);

  React.useEffect(() => {
    let isMounted = true;
    const playerContainer = playerContainerRef.current;

    loadYouTubeApi()
      .then((YT) => {
        if (!isMounted || !playerContainer || playerRef.current) {
          return;
        }

        const playerHost = document.createElement("div");
        playerContainer.replaceChildren(playerHost);

        const playerVars = {
          autoplay: 0,
          controls: 1,
          playsinline: 1,
          rel: 0
        };

        if (window.location.origin && window.location.origin !== "null") {
          playerVars.origin = window.location.origin;
        }

        playerRef.current = new YT.Player(playerHost, {
          width: "100%",
          height: "100%",
          videoId: loadedVideoIdRef.current,
          playerVars,
          events: {
            onReady: (event) => {
              playerReadyRef.current = true;
              event.target.mute?.();

              const videoChangedWhileLoading = loadedVideoIdRef.current !== videoIdRef.current;
              startPlayback(event.target, videoChangedWhileLoading);
            },
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                setHasStartedPlayback(true);
                autoplayRetryRef.current = false;

                if (pendingUnmuteRef.current) {
                  event.target.unMute?.();
                  pendingUnmuteRef.current = false;
                }
              }

              if (
                event.data === YT.PlayerState.ENDED &&
                isActiveRef.current &&
                isPlaybackEnabledRef.current
              ) {
                onPlaybackEndRef.current?.({
                  hasSound: event.target.isMuted?.() === false
                });
              }
            },
            onAutoplayBlocked: (event) => {
              if (
                autoplayRetryRef.current ||
                !isActiveRef.current ||
                !isPlaybackEnabledRef.current
              ) {
                return;
              }

              autoplayRetryRef.current = true;
              pendingUnmuteRef.current = false;
              const player = event?.target ?? playerRef.current;
              player?.mute?.();
              player?.playVideo?.();
            },
            onError: () => {
              if (
                errorVideoIdRef.current !== videoIdRef.current &&
                isActiveRef.current &&
                isPlaybackEnabledRef.current
              ) {
                errorVideoIdRef.current = videoIdRef.current;
                onPlaybackEndRef.current?.();
              }
            }
          }
        });
      })
      .catch(() => {});

    return () => {
      isMounted = false;
      playerReadyRef.current = false;
      playerRef.current?.destroy?.();
      playerRef.current = null;
      playerContainer?.replaceChildren();
    };
  }, [startPlayback]);

  React.useEffect(() => {
    if (!playerReadyRef.current || loadedVideoIdRef.current === videoId) {
      return;
    }

    setHasStartedPlayback(false);
    autoplayRetryRef.current = false;
    errorVideoIdRef.current = null;

    if (!isActive || !isPlaybackEnabled) {
      loadedVideoIdRef.current = videoId;
      playerRef.current?.cueVideoById?.(videoId);
      return;
    }

    startPlayback(playerRef.current, true);
  }, [isActive, isPlaybackEnabled, startPlayback, videoId]);

  React.useEffect(() => {
    if (!playerReadyRef.current) {
      return;
    }

    if (!isActive || !isPlaybackEnabled) {
      playerRef.current?.pauseVideo?.();
      return;
    }

    if (loadedVideoIdRef.current === videoIdRef.current) {
      startPlayback(playerRef.current, false);
    }
  }, [isActive, isPlaybackEnabled, startPlayback]);

  React.useEffect(() => {
    if (!isActive || !hasUserInteracted) {
      return;
    }

    pendingUnmuteRef.current = true;

    if (playerRef.current?.getPlayerState?.() === 1) {
      playerRef.current.unMute?.();
      pendingUnmuteRef.current = false;
    }
  }, [hasUserInteracted, isActive]);

  const showPreview = !isActive || !hasStartedPlayback;

  return (
    <div
      aria-label={title}
      className={className}
      data-active={isActive}
      role="group"
      style={{ position: "relative", overflow: "hidden" }}
    >
      {showPreview ? (
        <img
          alt={title}
          src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            pointerEvents: "none",
            zIndex: 1
          }}
        />
      ) : null}
      <div
        ref={playerContainerRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: isActive ? "auto" : "none"
        }}
      />
    </div>
  );
}

export function YouTubeEmbed({
  videoId,
  title,
  className = "",
  isActive = false,
  isPlaybackEnabled = true,
  onPlaybackEnd,
  hasUserInteracted = false,
  previewOnly = false
}) {
  if (previewOnly) {
    return <YouTubePreview className={className} title={title} videoId={videoId} />;
  }

  return (
    <YouTubePlayer
      className={className}
      hasUserInteracted={hasUserInteracted}
      isActive={isActive}
      isPlaybackEnabled={isPlaybackEnabled}
      onPlaybackEnd={onPlaybackEnd}
      title={title}
      videoId={videoId}
    />
  );
}
